import type { ReactNode } from 'react';
import { PrimaryButton, SecondaryButton } from '@ui/shared/primitives/FormControls';

/**
 * Shared chrome for the "manage this thing's lifecycle" footer pattern used
 * by both `HabitFormScreen.tsx`'s habit footer and
 * `ObjectiveLifecycleFooter.tsx` — a quiet secondary-weight text action plus
 * an inline (never `window.confirm`, never a modal/scrim) `alertdialog`
 * confirmation. Extracted because the two footers had drifted into
 * maintaining byte-for-byte copies of this markup independently; the two
 * screens' own lifecycle rules (habits: active/paused/archived; objectives:
 * active/archived only, plus a post-archive receipt) still live in each
 * screen, only the shared visual/aria chrome moved here.
 */
export function LifecycleFooterShell({ children }: { readonly children: ReactNode }) {
  return (
    <div style={{ marginTop: 'var(--space-8)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-subtle)' }}>
      {children}
    </div>
  );
}

export function FooterTextButton({ onClick, label }: { readonly onClick: () => void; readonly label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        color: 'var(--text-secondary)',
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        minHeight: 'var(--touch-target-min)',
      }}
    >
      {label}
    </button>
  );
}

export function LifecycleConfirmDialog({
  ariaLabel,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  readonly ariaLabel: string;
  readonly message: ReactNode;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-label={ariaLabel}
      style={{
        marginTop: 'var(--space-4)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-overlay)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-primary)', margin: 0 }}>{message}</p>
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
        <PrimaryButton onClick={onConfirm}>{confirmLabel}</PrimaryButton>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      </div>
    </div>
  );
}
